import type { UserConfig } from '@tunasong/manifest'
import type { ChangeEvent, FC } from 'react'
import { useId } from 'react'
import { useUserConfig } from '../config/config-service.hook.js'
import { FormControlLabel, Radio, RadioGroup, Typography } from '../primitives/index.js'
import styles from './theory-cues-setting.module.css'

/** `ui-lib` cannot import `music-ui`, so the level type comes from the config schema. */
type TheoryLevel = NonNullable<NonNullable<UserConfig['musicPreferences']>['theoryLevel']>

const options: { value: TheoryLevel; label: string }[] = [
  { value: 'off', label: 'Off' },
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
]

/** Mirrors the read-time default in music-ui's `useTheoryLevel`. */
const defaultLevel: TheoryLevel = 'intermediate'

function isTheoryLevel(value: string): value is TheoryLevel {
  return options.some(option => option.value === value)
}

/**
 * The "Theory cues" level for chord lines (song Main-layout declutter, PR 3).
 * Writes `musicPreferences.theoryLevel`, spreading the existing preferences so
 * transpose, voice motion and the rest are kept. Each change applies at once.
 */
export const TheoryCuesSetting: FC = () => {
  const [config, updateConfig] = useUserConfig()
  const titleId = useId()
  const helpId = useId()
  const name = useId()
  const level = config.musicPreferences?.theoryLevel ?? defaultLevel

  const handleChange = (_event: ChangeEvent<HTMLInputElement>, value: string) => {
    if (!isTheoryLevel(value)) {
      return
    }
    updateConfig('musicPreferences', { ...config.musicPreferences, theoryLevel: value })
  }

  return (
    <div className={styles.root}>
      <Typography variant="subtitle1" component="span" id={titleId} className={styles.title}>
        Theory cues
      </Typography>
      <RadioGroup name={name} value={level} onChange={handleChange} aria-labelledby={titleId} aria-describedby={helpId}>
        {options.map(option => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            control={<Radio size="small" />}
            label={option.label}
          />
        ))}
      </RadioGroup>
      <Typography variant="caption" id={helpId} className={styles.help}>
        Beginner and Intermediate mark chords outside the key and name cadences. Advanced adds Roman numerals and
        tension.
      </Typography>
    </div>
  )
}
